import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 597 (Kamel) — restrictions DISCRÈTES des modes de paiement (virement / CB / PayPal) par voyage ou par fiche.
// Iter 598 — date de fin facultative (incluse) : les modes réapparaissent tout seuls le lendemain.
export const MODES = [
  { k: 'virement', label: '🏦 Virement' },
  { k: 'cb', label: '💳 Carte' },
  { k: 'paypal', label: '🅿️ PayPal' },
  { k: 'wero', label: '📲 Wero' },
];
const LABEL = Object.fromEntries(MODES.map(m => [m.k, m.label]));
const today = () => new Date().toISOString().slice(0, 10);
export const restrictionActive = (off, fin) => (off || []).length > 0 && (!fin || fin >= today());
export const fmtFin = (fin) => (fin ? new Date(`${fin}T00:00:00`).toLocaleDateString('fr-FR') : '');
export const libelleOff = (off) => (off || []).map(k => LABEL[k] || k).join(' · ');

export const ChipsOff = ({ off, fin, onChange, testId, disabled }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
    {MODES.map(m => {
      const bloque = (off || []).includes(m.k);
      return (
        <button key={m.k} type="button" disabled={disabled} data-testid={`${testId}-${m.k}`}
          onClick={() => onChange(bloque ? off.filter(x => x !== m.k) : [...(off || []), m.k], fin)}
          title={bloque ? 'Bloqué : cliquer pour réautoriser' : 'Autorisé : cliquer pour bloquer discrètement'}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${bloque ? 'bg-red-600 text-white border-red-700 line-through' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}>
          {m.label}
        </button>
      );
    })}
    {(off || []).length > 0 && (
      <label className="inline-flex items-center gap-1 text-[11px] text-gray-600" title="Vide = sans fin. Sinon les modes réapparaissent tout seuls le lendemain de cette date.">
        ⏳ jusqu'au
        <input type="date" value={fin || ''} min={today()} disabled={disabled} data-testid={`${testId}-fin`}
          onChange={e => onChange(off, e.target.value)}
          className={`px-1.5 py-0.5 border rounded text-[11px] ${fin ? 'border-red-400 text-red-800 font-semibold bg-red-50' : 'border-gray-300'}`} />
        {fin && <button type="button" onClick={() => onChange(off, '')} data-testid={`${testId}-fin-clear`} className="text-gray-400 hover:text-red-700" title="Sans fin">✕</button>}
      </label>
    )}
  </div>
);

// Iter 599 — exceptions « ✅ autorisé malgré le voyage » (fiche loin de Lyon…) : vert = autorisé même si le voyage bloque.
export const ChipsOn = ({ on, off, onChange, testId, disabled }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
    {MODES.map(m => {
      const autorise = (on || []).includes(m.k);
      const bloqueFiche = (off || []).includes(m.k);
      return (
        <button key={m.k} type="button" disabled={disabled || bloqueFiche} data-testid={`${testId}-${m.k}`}
          onClick={() => onChange(autorise ? on.filter(x => x !== m.k) : [...(on || []), m.k])}
          title={bloqueFiche ? 'Bloqué sur la fiche elle-même' : autorise ? 'Autorisé malgré le voyage : cliquer pour retirer l\'exception' : 'Cliquer pour autoriser ce mode même si le voyage le bloque'}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${autorise ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-gray-500 border-dashed border-gray-300 hover:border-emerald-500'} ${bloqueFiche ? 'opacity-40' : ''}`}>
          {autorise ? '✅ ' : ''}{m.label}
        </button>
      );
    })}
  </div>
);

export const effectifOff = (reg, parVoyage) => {
  const fiche = restrictionActive(reg.paiements_off, reg.paiements_off_fin) ? reg.paiements_off : [];
  const on = new Set(reg.paiements_on || []);
  return { fiche, voyage: (parVoyage?.off || []).filter(k => !on.has(k)) };
};

// Cases « modes bloqués pour cette fiche » (panneau 🔔 Rappels).
export const PaiementsOffFiche = ({ reg, getAuthConfig, setRegistrations, parVoyage }) => {
  const [busy, setBusy] = useState(false);
  const off = reg.paiements_off || [];
  const fin = reg.paiements_off_fin || '';
  const on = reg.paiements_on || [];
  const expiree = off.length > 0 && !restrictionActive(off, fin);
  const save = async (nextOff, nextFin, nextOn = on) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/moyens-paiement/fiche/${reg.id}`, { off: nextOff, fin: nextFin || '', on: nextOn }, getAuthConfig());
      setRegistrations?.(prev => prev.map(x => (x.id === reg.id ? { ...x, paiements_off: r.data.off, paiements_off_fin: r.data.fin, paiements_on: r.data.on } : x)));
    } catch (e) { alert('Erreur : ' + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };
  const voyageOff = parVoyage?.off || [];
  return (
    <div className="space-y-1.5 text-[11px]" data-testid={`paiements-off-fiche-${reg.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={off.length && !expiree ? 'text-red-700 font-semibold' : 'text-gray-600'} title="Le mode disparaît simplement de son Espace Adhérent, sans aucune mention">
          💳 Modes bloqués pour cette fiche{off.length && !expiree ? ` (${off.length})` : ' (discret — il ne restera que les espèces sur RDV)'} :
        </span>
        <ChipsOff off={off} fin={fin} onChange={(o, f) => save(o, f)} disabled={busy} testId={`paiements-off-fiche-chips-${reg.id}`} />
        {expiree && <span className="text-gray-500 italic" data-testid={`paiements-off-fiche-expiree-${reg.id}`}>(restriction terminée le {fmtFin(fin)} — modes de nouveau visibles)</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={on.length ? 'text-emerald-700 font-semibold' : 'text-gray-600'} title="Exception : cette personne (loin de Lyon…) garde ce mode même si son voyage le bloque. Le blocage global reste prioritaire.">
          ✅ Autorisé malgré le voyage{voyageOff.length ? ` (voyage bloque : ${libelleOff(voyageOff)})` : ''} :
        </span>
        <ChipsOn on={on} off={off} onChange={(o) => save(off, fin, o)} disabled={busy} testId={`paiements-on-fiche-chips-${reg.id}`} />
      </div>
    </div>
  );
};

// Section « 🎯 Restrictions par voyage » + fiches restreintes (onglet 💳 Moyens de paiement).
const eur = (n) => `${(Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

export const PaiementsOffSection = ({ getAuthConfig }) => {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({});
  const load = useCallback(() => axios.get(`${API}/admin/moyens-paiement/surcharges`, getAuthConfig()).then(r => setData(r.data)).catch(() => setData({ voyages: [], fiches: [] })), [getAuthConfig]);
  const loadStats = useCallback(() => axios.get(`${API}/admin/moyens-paiement/stats-especes`, getAuthConfig()).then(r => setStats(Object.fromEntries((r.data.voyages || []).map(v => [v.id, v])))).catch(() => {}), [getAuthConfig]);
  useEffect(() => { load(); loadStats(); }, [load, loadStats]);
  const saveVoyage = async (id, off, fin) => {
    try {
      const r = await axios.post(`${API}/admin/moyens-paiement/voyage/${id}`, { off, fin: fin || '' }, getAuthConfig());
      setData(d => ({ ...d, voyages: d.voyages.map(v => (v.id === id ? { ...v, off: r.data.off, fin: r.data.fin } : v)) }));
      loadStats();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.detail || e.message)); }
  };
  const saveFiche = async (id, off, fin, on) => {
    try {
      const r = await axios.post(`${API}/admin/moyens-paiement/fiche/${id}`, { off, fin: fin || '', on: on || [] }, getAuthConfig());
      setData(d => ({ ...d, fiches: d.fiches.map(f => (f.id === id ? { ...f, off: r.data.off, fin: r.data.fin, on: r.data.on } : f)).filter(f => f.off.length || f.on.length) }));
    } catch (e) { alert('Erreur : ' + (e.response?.data?.detail || e.message)); }
  };
  if (!data) return <p className="text-sm text-gray-400">Chargement des restrictions…</p>;
  const nbRestreints = data.voyages.filter(v => v.off.length).length;
  return (
    <div className="bg-white rounded-2xl p-4 border-2 border-red-200 space-y-3" data-testid="paiements-off-section">
      <div>
        <h3 className="text-base font-bold text-[#1E3A5F]">🎯 Restrictions discrètes par voyage</h3>
        <p className="text-xs text-gray-600 mt-1">Pour la trésorerie : bloque le virement, la carte ou PayPal sur un voyage précis → ces modes <strong>disparaissent</strong> de l'Espace Adhérent des inscrits de ce voyage, <strong>sans aucune mention</strong> (il leur reste courrier + espèces sur rendez-vous, les espèces étant mises en avant). Une date « ⏳ jusqu'au » rend la restriction temporaire : tout revient tout seul le lendemain. Le blocage global ci-dessus reste prioritaire. <strong data-testid="paiements-off-nb-voyages">{nbRestreints} voyage(s) restreint(s)</strong>.</p>
      </div>
      <div className="divide-y divide-gray-100" data-testid="paiements-off-voyages">
        {data.voyages.map(v => (
          <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`paiements-off-voyage-${v.id}`}>
            <span className="text-sm text-gray-800"><span className="text-gray-500 text-xs mr-1">📅 {v.debut ? new Date(v.debut).toLocaleDateString('fr-FR') : ''}</span><strong>{v.titre}</strong>{v.brouillon && <span className="ml-1 text-[10px] text-gray-500">(brouillon)</span>}
              {v.off.length > 0 && <span className="ml-2 text-[10px] text-red-700 font-semibold" data-testid={`paiements-off-voyage-nb-${v.id}`}>{v.reg_ids?.length ?? 0} fiche(s) concernée(s)</span>}</span>
            <ChipsOff off={v.off} fin={v.fin} onChange={(off, fin) => saveVoyage(v.id, off, fin)} testId={`paiements-off-chips-${v.id}`} />
            {v.off.length > 0 && stats[v.id]?.apres && (
              <p className="w-full text-[11px] text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5" data-testid={`paiements-off-stats-${v.id}`}>
                💵 Espèces depuis la restriction ({fmtFin(stats[v.id].depuis)}) : <strong className="text-emerald-800">{stats[v.id].apres.part} %</strong> ({eur(stats[v.id].apres.especes)} sur {eur(stats[v.id].apres.total)}, {stats[v.id].apres.nb_especes}/{stats[v.id].apres.nb} règlements)
                {' · '}avant : {stats[v.id].avant.part} % ({eur(stats[v.id].avant.especes)} sur {eur(stats[v.id].avant.total)})
              </p>
            )}
          </div>
        ))}
        {!data.voyages.length && <p className="text-xs text-gray-400 py-2">Aucun voyage à venir.</p>}
      </div>
      <div data-testid="paiements-off-fiches">
        <h4 className="text-sm font-bold text-[#1E3A5F]">👤 Fiches réglées individuellement <span className="text-xs font-normal text-gray-500">(depuis la fiche → 🔔 Rappels : bloqué en rouge, ✅ autorisé malgré le voyage en vert)</span></h4>
        {!data.fiches.length && <p className="text-xs text-gray-400 mt-1">Aucune fiche restreinte ni exception.</p>}
        {data.fiches.map(f => (
          <div key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 border-b border-gray-100 last:border-0" data-testid={`paiements-off-fiche-ligne-${f.id}`}>
            <span className="text-sm text-gray-800"><strong>{f.nom}</strong> <span className="text-xs text-gray-500">· {f.voyage}</span></span>
            <div className="flex flex-wrap items-center gap-3">
              <ChipsOff off={f.off} fin={f.fin} onChange={(off, fin) => saveFiche(f.id, off, fin, f.on)} testId={`paiements-off-fiche-chips-${f.id}`} />
              <ChipsOn on={f.on || []} off={f.off} onChange={(on) => saveFiche(f.id, f.off, f.fin, on)} testId={`paiements-on-fiche-chips-${f.id}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Badge « 💳 restreint » sur la carte Inscriptions : fiche OU voyage restreint (voyages via GET surcharges).
export const useRestrictionsVoyages = (getAuthConfig) => {
  const [map, setMap] = useState({});
  useEffect(() => {
    axios.get(`${API}/admin/moyens-paiement/surcharges`, getAuthConfig()).then(r => {
      const m = {};
      (r.data.voyages || []).forEach(v => v.off.length && (v.reg_ids || []).forEach(id => { m[id] = { off: v.off, fin: v.fin, voyage: v.titre }; }));
      setMap(m);
    }).catch(() => {});
  }, [getAuthConfig]);
  return map;
};

export const BadgeRestreint = ({ reg, parVoyage, onClick }) => {
  const { fiche, voyage } = effectifOff(reg, parVoyage);
  if (!fiche.length && !voyage.length) return null;
  const tips = [fiche.length && `Fiche : ${libelleOff(fiche)}${reg.paiements_off_fin ? ` (jusqu'au ${fmtFin(reg.paiements_off_fin)})` : ''}`,
    voyage.length && `Voyage : ${libelleOff(voyage)}${parVoyage.fin ? ` (jusqu'au ${fmtFin(parVoyage.fin)})` : ''}`].filter(Boolean);
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick?.(); }} data-testid={`restreint-badge-${reg.id}`}
      title={`Modes de paiement masqués dans son Espace Adhérent (discret) — ${tips.join(' · ')}. Clique pour ouvrir 🔔 Rappels.`}
      className="text-xs bg-red-50 text-red-800 px-2 py-0.5 rounded-full font-bold border border-red-300 hover:bg-red-100 transition">
      💳 restreint{fiche.length && voyage.length ? ' (fiche + voyage)' : voyage.length ? ' (voyage)' : ''}
    </button>
  );
};
